import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PublicLayout } from '../../components/PublicLayout';
import { useBooking } from '../../contexts/BookingContext';
import { publicAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { ReservationSearch } from '../../components/ReservationSearch';
import { format } from 'date-fns';
import {
  Building2,
  Star,
  Shield,
  Clock,
  ArrowRight,
} from 'lucide-react';
import { toast } from 'sonner';

const heroImages = [
  'https://images.unsplash.com/photo-1715191904112-4a5d9c3089fa?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBob3RlbCUyMGV4dGVyaW9yJTIwYXJjaGl0ZWN0dXJlJTIwcmVzb3J0fGVufDB8fHx8MTc3MzkzODYxNnww&ixlib=rb-4.1.0&q=85',
];

const features = [
  {
    icon: Building2,
    title: 'Handpicked Properties',
    description: 'Carefully curated collection of premium hotels and resorts.',
  },
  {
    icon: Shield,
    title: 'Best Price Guarantee',
    description: 'We match any lower price you find elsewhere.',
  },
  {
    icon: Clock,
    title: 'Instant Confirmation',
    description: 'Book now and receive immediate booking confirmation.',
  },
  {
    icon: Star,
    title: '24/7 Support',
    description: 'Our team is always ready to assist with your needs.',
  },
];

export default function Home() {
  const navigate = useNavigate();
  const { updateSearch, setSearchResults } = useBooking();
  const [loading, setLoading] = useState(false);

  const handleSearch = async ({ checkIn, checkOut, guests }) => {
    if (!checkIn || !checkOut) {
      toast.error('Please select check-in and check-out dates');
      return;
    }

    setLoading(true);
    try {
      const searchData = {
        check_in: format(checkIn, 'yyyy-MM-dd'),
        check_out: format(checkOut, 'yyyy-MM-dd'),
        guests: parseInt(guests),
      };

      const response = await publicAPI.searchAvailability(searchData);
      updateSearch({
        checkIn: searchData.check_in,
        checkOut: searchData.check_out,
        guests: searchData.guests,
      });
      setSearchResults(response.data);
      navigate('/search');
    } catch (error) {
      toast.error('Failed to search availability');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      {/* Hero Section */}
      <section className="relative h-[90vh] min-h-[600px] flex items-center" data-testid="hero-section">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImages[0]})` }}
        >
          <div className="absolute inset-0 hero-overlay" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl animate-slide-up">
            <p className="text-white/80 text-sm uppercase tracking-widest mb-4">
              Luxury Accommodations
            </p>
            <h1 className="text-5xl md:text-7xl font-serif text-white font-medium tracking-tight leading-none mb-6">
              Experience <br />
              <span className="italic">Exceptional</span> Stays
            </h1>
            <p className="text-xl text-white/80 leading-relaxed mb-12 max-w-lg">
              Discover our curated collection of premium hotels and create unforgettable memories.
            </p>

            {/* Search Box */}
            <ReservationSearch
              onSearch={handleSearch}
              loading={loading}
              showTitle={true}
              showGuests={true}
              className="max-w-md animate-fade-in"
              style={{ animationDelay: '0.3s' }}
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-bone">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-primary text-sm uppercase tracking-widest mb-4">Why Choose Us</p>
            <h2 className="text-4xl md:text-5xl font-serif font-medium tracking-tight">
              The Resident Hotels Difference
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="text-center p-8 animate-slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold mb-3">{feature.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-primary rounded-3xl p-12 md:p-16 text-center">
            <h2 className="text-3xl md:text-4xl font-serif text-white font-medium mb-6">
              Ready to book your perfect stay?
            </h2>
            <p className="text-white/80 text-lg mb-8 max-w-2xl mx-auto">
              Browse our collection of handpicked properties and find your ideal accommodation.
            </p>
            <Button
              onClick={() => navigate('/properties')}
              variant="secondary"
              size="lg"
              className="rounded-full px-8 py-6 text-lg"
              data-testid="browse-properties-btn"
            >
              Browse Properties
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
